import { getMap, MAP_VERSION } from './maps.js';
import {
  DOCKED_DISTANCE,
  PHYSICS,
  PHYSICS_VERSION,
  collisionAlongSegment,
  newHook,
  positionAt,
} from './physics.js';
import type { DateKey, Hook, Mineral, MineralKind } from './types.js';

export const GOLD_ENGINE_VERSION = 'gold.v2';
export const GOLD_RULES_VERSION = 'gold-v2.2-final-20261103';
export const GOLD_MAP_VERSION = MAP_VERSION;
export const GOLD_V2_TICKS = 1350;

const GOLD_VALUES: Readonly<Record<MineralKind, number>> = Object.freeze({
  small: 10,
  medium: 20,
  large: 30,
  treasure: 30,
  stone: 0,
  box: 0,
  brand: 0,
});
const LIVE_KINDS = new Set<MineralKind>(['small', 'medium', 'large', 'stone']);
const MAX_TICK = Number.MAX_SAFE_INTEGER;

export type GoldPhase = 'RUNNING' | 'SETTLED' | 'EXPIRED';
export interface GoldV2State {
  engineVersion: typeof GOLD_ENGINE_VERSION;
  rulesVersion: typeof GOLD_RULES_VERSION;
  mapVersion: typeof GOLD_MAP_VERSION;
  physicsVersion: typeof PHYSICS_VERSION;
  dateKey: DateKey;
  tick: number;
  dayEndTick: number;
  phase: GoldPhase;
  goldAmount: number;
  shots: number;
  lastSeq: number;
  objects: Mineral[];
  hook: Hook;
}
export interface GoldRunOptions {
  dateKey: DateKey;
  dayEndTick?: number;
  mapVersion?: typeof GOLD_MAP_VERSION;
  physicsVersion?: typeof PHYSICS_VERSION;
  rulesVersion?: typeof GOLD_RULES_VERSION;
}
export interface GoldInput {
  seq: number;
  tick: number;
  type: 'release';
}
export interface GoldEvent {
  tick: number;
  type: 'release' | 'catch' | 'return' | 'settled' | 'expired';
  objectId?: number;
  kind?: MineralKind;
  goldAmount?: number;
}
export interface GoldAdvanceResult {
  state: GoldV2State;
  events: GoldEvent[];
}
export interface GoldInputResult extends GoldAdvanceResult {
  accepted: boolean;
  reason?: string;
}
export interface GoldReplayOptions {
  toTick?: number;
  dayEndTick?: number;
}

function integer(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error(`Invalid ${label}`);
}
function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
function terminal(state: GoldV2State): boolean {
  return state.phase !== 'RUNNING';
}
function event(events: GoldEvent[], state: GoldV2State, data: Omit<GoldEvent, 'tick'>): void {
  events.push({ ...data, tick: state.tick });
}
function finish(state: GoldV2State, events: GoldEvent[], phase: 'SETTLED' | 'EXPIRED'): void {
  state.phase = phase;
  state.hook = newHook();
  event(events, state, { type: phase === 'SETTLED' ? 'settled' : 'expired', goldAmount: state.goldAmount });
}
function boundary(state: GoldV2State, events: GoldEvent[]): void {
  if (terminal(state)) return;
  if (state.tick >= state.dayEndTick) {
    finish(state, events, 'EXPIRED');
    return;
  }
  if (state.tick >= GOLD_V2_TICKS) finish(state, events, 'SETTLED');
}
function completeReturn(state: GoldV2State, events: GoldEvent[]): void {
  const object = state.hook.carry;
  if (object) {
    state.goldAmount += GOLD_VALUES[object.kind];
    event(events, state, { type: 'return', objectId: object.id, kind: object.kind, goldAmount: state.goldAmount });
  } else {
    event(events, state, { type: 'return', goldAmount: state.goldAmount });
  }
  state.hook.status = 'SWINGING';
  state.hook.carry = null;
  state.hook.shotPhase = null;
  state.hook.fastReturn = false;
}
function simulate(state: GoldV2State, events: GoldEvent[]): void {
  const hook = state.hook;
  if (hook.status === 'SWINGING') {
    hook.angle += PHYSICS.swingSpeed * hook.direction;
    if (hook.angle > PHYSICS.maxAngle) {
      hook.angle = 2 * PHYSICS.maxAngle - hook.angle;
      hook.direction = -1;
    }
    if (hook.angle < PHYSICS.minAngle) {
      hook.angle = 2 * PHYSICS.minAngle - hook.angle;
      hook.direction = 1;
    }
    Object.assign(hook, positionAt(hook.angle, DOCKED_DISTANCE));
    return;
  }
  if (hook.status === 'EXTENDING') {
    const previous = { x: hook.x, y: hook.y };
    const previousDistance = hook.distance;
    hook.distance = Math.min(PHYSICS.maxDistance - DOCKED_DISTANCE, hook.distance + PHYSICS.extensionSpeed);
    const next = positionAt(hook.angle, DOCKED_DISTANCE + hook.distance);
    Object.assign(hook, next);
    const hit = collisionAlongSegment(previous, next, state.objects);
    if (hit) {
      hook.carry = hit.object;
      state.objects = state.objects.filter(candidate => candidate.id !== hit.object.id);
      hook.distance = Math.max(0, previousDistance + Math.trunc((hook.distance - previousDistance) * hit.t));
      Object.assign(hook, hit.point);
      hook.status = 'RETRACTING';
      event(events, state, { type: 'catch', objectId: hit.object.id, kind: hit.object.kind });
    } else if (hook.distance >= PHYSICS.maxDistance - DOCKED_DISTANCE || hook.x <= PHYSICS.minX || hook.x >= PHYSICS.maxX || hook.y >= PHYSICS.maxY) {
      hook.status = 'RETRACTING';
    }
    return;
  }
  const speed = hook.carry ? PHYSICS.returnSpeeds[hook.carry.kind] : PHYSICS.emptyReturnSpeed;
  hook.distance = Math.max(0, hook.distance - speed);
  Object.assign(hook, positionAt(hook.angle, DOCKED_DISTANCE + hook.distance));
  if (hook.distance === 0) completeReturn(state, events);
}

export function createGoldRun(options: GoldRunOptions): GoldV2State {
  if (options.mapVersion && options.mapVersion !== GOLD_MAP_VERSION) throw new Error('Unknown map version');
  if (options.physicsVersion && options.physicsVersion !== PHYSICS_VERSION) throw new Error('Unknown physics version');
  if (options.rulesVersion && options.rulesVersion !== GOLD_RULES_VERSION) throw new Error('Unknown rules version');
  const dayEndTick = options.dayEndTick ?? MAX_TICK;
  integer(dayEndTick, 'dayEndTick');
  // The final integrated campaign has one immutable map for every activity day.
  const map = { ...getMap('D1', GOLD_MAP_VERSION), dateKey: options.dateKey };
  return {
    engineVersion: GOLD_ENGINE_VERSION,
    rulesVersion: GOLD_RULES_VERSION,
    mapVersion: GOLD_MAP_VERSION,
    physicsVersion: PHYSICS_VERSION,
    dateKey: options.dateKey,
    tick: 0,
    dayEndTick,
    phase: dayEndTick === 0 ? 'EXPIRED' : 'RUNNING',
    goldAmount: 0,
    shots: 0,
    lastSeq: 0,
    objects: clone(map.A.filter(object => LIVE_KINDS.has(object.kind))),
    hook: newHook(),
  };
}

/** Target ticks are exclusive: only ticks strictly below targetTick are simulated. */
export function advanceGoldTo(original: GoldV2State, targetTick: number, dayEndTick = original.dayEndTick): GoldAdvanceResult {
  integer(targetTick, 'targetTick');
  integer(dayEndTick, 'dayEndTick');
  if (targetTick < original.tick) throw new Error('Tick cannot move backwards');
  const state = clone(original);
  state.dayEndTick = Math.min(state.dayEndTick, dayEndTick);
  const events: GoldEvent[] = [];
  boundary(state, events);
  while (state.tick < targetTick && !terminal(state)) {
    simulate(state, events);
    state.tick += 1;
    boundary(state, events);
  }
  return { state, events };
}

function rejection(state: GoldV2State, reason: string): GoldInputResult {
  return { state, events: [], accepted: false, reason };
}

export function applyGoldInput(original: GoldV2State, input: GoldInput, dayEndTick = original.dayEndTick): GoldInputResult {
  if (!Number.isSafeInteger(input.seq) || input.seq !== original.lastSeq + 1) return rejection(original, 'sequence');
  if (!Number.isSafeInteger(input.tick) || input.tick < original.tick) return rejection(original, 'tick');
  if (input.type !== 'release') return rejection(original, 'input_type');
  if (terminal(original)) return rejection(original, 'terminal');
  const advanced = advanceGoldTo(original, input.tick, dayEndTick);
  const state = advanced.state;
  if (terminal(state)) return rejection(original, 'terminal');
  if (state.hook.status !== 'SWINGING') return rejection(original, 'hook_busy');
  state.shots += 1;
  state.lastSeq = input.seq;
  state.hook.status = 'EXTENDING';
  Object.assign(state.hook, positionAt(state.hook.angle, DOCKED_DISTANCE));
  state.hook.shotPhase = 'A';
  state.hook.fastReturn = false;
  event(advanced.events, state, { type: 'release' });
  return { state, events: advanced.events, accepted: true };
}

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value)
      .sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0)
      .map(([key, nested]) => [key, canonical(nested)]));
  }
  return value;
}
export function serializeGoldRun(state: GoldV2State): string {
  return JSON.stringify(canonical(state));
}
export function deserializeGoldRun(blob: string): GoldV2State {
  const state = JSON.parse(blob) as GoldV2State;
  const checkNumbers = (value: unknown): void => {
    if (typeof value === 'number' && !Number.isSafeInteger(value)) throw new Error('Checkpoint contains non-integer state');
    if (value && typeof value === 'object') Object.values(value).forEach(checkNumbers);
  };
  checkNumbers(state);
  if (state.engineVersion !== GOLD_ENGINE_VERSION || state.rulesVersion !== GOLD_RULES_VERSION || state.mapVersion !== GOLD_MAP_VERSION || state.physicsVersion !== PHYSICS_VERSION) {
    throw new Error('Invalid gold checkpoint version');
  }
  if (!['RUNNING', 'SETTLED', 'EXPIRED'].includes(state.phase)) throw new Error('Invalid gold checkpoint phase');
  for (const field of ['tick', 'dayEndTick', 'goldAmount', 'shots', 'lastSeq'] as const) integer(state[field], field);
  if (!state.hook || !['SWINGING', 'EXTENDING', 'RETRACTING'].includes(state.hook.status)) throw new Error('Invalid gold checkpoint hook');
  return state;
}
export function replayGoldRun(initial: GoldV2State, inputs: readonly GoldInput[], options: GoldReplayOptions = {}): GoldV2State {
  let state = clone(initial);
  const dayEndTick = options.dayEndTick ?? state.dayEndTick;
  for (const input of inputs) {
    const result = applyGoldInput(state, input, dayEndTick);
    if (!result.accepted) throw new Error(`Gold replay rejected input ${input.seq}: ${result.reason}`);
    state = result.state;
  }
  if (options.toTick !== undefined) state = advanceGoldTo(state, options.toTick, dayEndTick).state;
  return state;
}
